import React, { FC, useState } from "react"
import { Card, Descriptions, Tag, Typography } from "antd"
import { ColumnsType } from "antd/lib/table"
import classnames from "classnames"
import { useTranslation } from "react-i18next"
import { CardHeader, CourseCardItem, descriptionStyle } from "../CoursesTable/CourseCard"
import { margin } from "../../styles/layout"
import cardCss from "../CoursesTable/CourseTable.module.css"
import { InterestListItem } from "./interestListData"
import { groupInterestLists, splitIntoAgeBands } from "./interestGroups"
import { EMPTY, renderInterestCell } from "./interestRowHelpers"
import InterestDetailsButton from "./InterestDetailsButton"
import { formatTimeSlotsShort } from "./timeSlotSelection"
import css from "./InterestListSections.module.css"

// Lange Texte (Beschreibung, viele Zeitfenster) auf zwei Zeilen klemmen, sonst
// sprengt eine Karte die ganze Rasterzeile.
// ponytail: Faustregel statt Messung — kürzer als ~110 Zeichen passt in zwei Zeilen.
// AntDs Paragraph-ellipsis misst per ResizeObserver und löst im Raster eine
// „ResizeObserver loop“-Fehlermeldung aus; CSS line-clamp kommt ohne aus.
const LONG_DESCRIPTION = 110

const ClampedText: FC<{ text: string }> = ({ text }) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState(false)
    const isLong = text.length > LONG_DESCRIPTION
    return (
        <div className={css.description}>
            <div className={isLong && !expanded ? css.clamped : undefined}>{text}</div>
            {isLong && (
                <Typography.Link onClick={() => setExpanded(!expanded)}>
                    {expanded ? t("interest.readLess", "Weniger") : t("interest.readMore", "Mehr lesen")}
                </Typography.Link>
            )}
        </div>
    )
}

type InterestCardProps = {
    list: InterestListItem
    // Spalten der Kursliste des Tenants: dieselben Zeilen wie auf der Kurskarte.
    columnsToUse: ColumnsType<CourseCardItem>
    className?: string
}

// Karte je Interessenten-Liste im Aufbau der Kurskarte: Kopf in Kursart-Farbe,
// dieselben Zeilen aus den Kurs-Spalten (nur die mit Inhalt), Button in voller
// Breite. Steht im Desktop-Raster als Kachel hinter den Kurskarten, mobil in
// Altersabschnitten.
export const InterestCard: FC<InterestCardProps> = ({ list, columnsToUse, className }) => {
    const { t } = useTranslation()
    const color = list.courseType.color ?? undefined
    return (
        <Card
            id={`interest-${list.id}`}
            size='small'
            bordered={false}
            className={classnames(cardCss.card, className)}
            data-testid='interest-card'
            headStyle={{ background: color }}
            title={<CardHeader courseType={{ name: list.courseType.name, color }} prefixedCourseNumber={t("interest.badge", "Interessenten-Liste")} />}
        >
            <Descriptions
                bordered={descriptionStyle.bordered}
                size='small'
                column={1}
                contentStyle={descriptionStyle.contentStyle}
                labelStyle={descriptionStyle.labelStyle}
                className={cardCss.descriptions}
            >
                {columnsToUse.map((column) => {
                    const key = column.key as string
                    if (key === "courseType" || key === "actionColumn") return null
                    const content =
                        key === "additionalShortInfo"
                            ? list.description && <ClampedText text={list.description} />
                            : key === "startDateTime"
                            ? list.timeSlots?.length && <ClampedText text={formatTimeSlotsShort(list.timeSlots)} />
                            : renderInterestCell(key, list, t)
                    // Kurs-Spalten ohne Wert für eine Liste (Preis, Plätze, …) bekommen keine Zeile.
                    if (!content || content === EMPTY) return null
                    const label = key === "startDateTime" ? t("interest.timeSlotsLabel", "Mögliche Zeiten") : (column.title as string)
                    return (
                        <Descriptions.Item key={key} label={label}>
                            {content}
                        </Descriptions.Item>
                    )
                })}
            </Descriptions>
            {/* Button wie auf der Kurskarte: volle Breite, am unteren Rand. */}
            <div className={cardCss.cardFooter}>
                <InterestDetailsButton list={list} style={{ margin: -5, marginTop: margin.s, flex: 1, width: "100%" }} />
            </div>
        </Card>
    )
}

type InterestListSectionsProps = {
    lists: ReadonlyArray<InterestListItem>
    columnsToUse: ColumnsType<CourseCardItem>
}

/** Mobile Interessenten-Listen: eine Karte je Liste, nach Alter sortiert, in Altersabschnitten. */
const InterestListSections: FC<InterestListSectionsProps> = ({ lists, columnsToUse }) => {
    const { t } = useTranslation()
    const [selectedBand, setSelectedBand] = useState<string | null>(null)
    const bands = splitIntoAgeBands(groupInterestLists(lists)).map((band) => ({
        ...band,
        lists: band.groups.reduce((all, group) => all.concat(group.lists), [] as Array<InterestListItem>),
    }))
    // Abschnitte und Schnellwahl lohnen erst ab zwei Altersgruppen.
    const showSections = bands.length > 1
    // Nach dem Blättern kann die gewählte Gruppe fehlen: dann wie "Alle", sonst bliebe die Seite leer ohne sichtbaren Chip.
    const activeBand = showSections && bands.some((band) => band.key === selectedBand) ? selectedBand : null
    const visibleBands = bands.filter((band) => !activeBand || band.key === activeBand)

    return (
        <div data-testid='interest-list-sections' style={{ width: "100%" }}>
            {showSections && (
                <div className={css.chips}>
                    <Tag.CheckableTag checked={!activeBand} onChange={() => setSelectedBand(null)}>
                        {t("interest.allAgeBands", "Alle")}
                    </Tag.CheckableTag>
                    {bands.map((band) => (
                        <Tag.CheckableTag key={band.key} checked={activeBand === band.key} onChange={(checked) => setSelectedBand(checked ? band.key : null)}>
                            {t(`interest.ageBandChip.${band.key}`, band.chip)}
                        </Tag.CheckableTag>
                    ))}
                </div>
            )}
            {visibleBands.map((band) => (
                <section key={band.key} data-testid='interest-age-band'>
                    {showSections && (
                        <div className={css.section}>
                            {t(`interest.ageBand.${band.key}`, band.title)}
                            <span className={css.sectionCount}>{band.lists.length}</span>
                        </div>
                    )}
                    {band.lists.map((list) => (
                        <InterestCard key={list.id} list={list} columnsToUse={columnsToUse} />
                    ))}
                </section>
            ))}
        </div>
    )
}

export default InterestListSections
